import React, { useCallback, useEffect, useState } from 'react';
import { apiClient } from '../../api/client';
import { logger } from '../../lib/logger';
import { Send, Check, X, Copy, Loader2, Bell, Link2Off, TestTube2 } from 'lucide-react';
import { toast } from 'sonner';

/**
 * Telegram notification pairing + management card.
 * - "Bağlan" → fetches a 6-digit pairing code & bot username
 *   → user types /baglan <code> to the bot → backend links the chat_id.
 * - Once linked, shows status + test message + unlink buttons.
 */
export default function TelegramLinkCard() {
    const [status, setStatus] = useState(null); // null = loading, {linked, bot_available, ...}
    const [pairData, setPairData] = useState(null); // {code, bot_username, expires_in}
    const [pairingCountdown, setPairingCountdown] = useState(0);
    const [loading, setLoading] = useState(false);

    const loadStatus = useCallback(async () => {
        try {
            const { data } = await apiClient.get('/telegram/status');
            setStatus(data);
            // If just linked while waiting for pairing, clear the code
            if (data?.linked) setPairData(null);
        } catch (e) {
            logger.warn('telegram status failed', e);
            setStatus({ linked: false, bot_available: false });
        }
    }, []);

    useEffect(() => {
        loadStatus();
        const id = setInterval(loadStatus, 5000); // poll every 5s to catch linkage
        return () => clearInterval(id);
    }, [loadStatus]);

    // Pair-code countdown
    useEffect(() => {
        if (!pairData || pairingCountdown <= 0) return;
        const id = setInterval(() => setPairingCountdown((c) => Math.max(0, c - 1)), 1000);
        return () => clearInterval(id);
    }, [pairData, pairingCountdown]);

    const startPair = async () => {
        setLoading(true);
        try {
            const { data } = await apiClient.post('/telegram/pair');
            setPairData(data);
            setPairingCountdown(data.expires_in || 600);
        } catch (e) {
            toast.error(e?.response?.data?.detail || 'Kod oluşturulamadı');
        } finally {
            setLoading(false);
        }
    };

    const unlink = async () => {
        if (!window.confirm('Telegram bağlantısı kaldırılacak. Devam?')) return;
        try {
            await apiClient.delete('/telegram/unlink');
            toast.success('Bağlantı kaldırıldı');
            setPairData(null);
            loadStatus();
        } catch (e) {
            toast.error(e?.response?.data?.detail || 'Kaldırılamadı');
        }
    };

    const sendTest = async () => {
        setLoading(true);
        try {
            await apiClient.post('/telegram/test');
            toast.success('Test mesajı Telegram\'a gönderildi 📲');
        } catch (e) {
            toast.error(e?.response?.data?.detail || 'Mesaj gönderilemedi');
        } finally {
            setLoading(false);
        }
    };

    const copyCode = () => {
        if (!pairData?.code) return;
        navigator.clipboard?.writeText(pairData.code);
        toast.success('Kod kopyalandı');
    };

    const copyCommand = () => {
        if (!pairData?.code) return;
        navigator.clipboard?.writeText(`/baglan ${pairData.code}`);
        toast.success('Komut kopyalandı');
    };

    if (status === null) {
        return (
            <div className="border border-slate-700 bg-slate-800 rounded-xl p-4 flex items-center gap-2 text-slate-400 text-xs">
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
                Telegram durumu yükleniyor…
            </div>
        );
    }

    if (!status.bot_available) {
        return (
            <div
                data-testid="telegram-card-disabled"
                className="border border-slate-700 bg-slate-800 rounded-xl p-4 text-xs text-slate-400"
            >
                <div className="flex items-center gap-2 mb-1">
                    <Send className="h-4 w-4 text-slate-500" />
                    <span className="font-semibold text-slate-200">Telegram Bildirimleri</span>
                </div>
                Bot şu an aktif değil (sunucu ayarları eksik).
            </div>
        );
    }

    return (
        <div
            data-testid="telegram-card"
            className="border border-sky-500/25 bg-gradient-to-br from-sky-950/25 via-slate-800 to-slate-800 rounded-xl p-4"
        >
            <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2.5">
                    <div className="h-8 w-8 rounded-full bg-sky-500/15 border border-sky-500/30 flex items-center justify-center">
                        <Send className="h-4 w-4 text-sky-300" />
                    </div>
                    <div>
                        <div className="text-sm font-semibold text-slate-100">Telegram Bildirimleri</div>
                        <div className="text-[11px] text-slate-400">
                            TP/SL ve yüksek ihtimal fırsatları telefonuna anlık push
                        </div>
                    </div>
                </div>
                {status.linked && (
                    <span className="inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wider text-emerald-300 bg-emerald-500/15 border border-emerald-500/30 rounded-full px-2 py-0.5">
                        <Check className="h-3 w-3" /> Bağlı
                    </span>
                )}
            </div>

            {status.linked ? (
                <div className="space-y-3">
                    <div className="text-[11px] text-slate-300 bg-slate-700/40 border border-slate-700 rounded-lg px-3 py-2">
                        Hesap: <span className="font-mono text-slate-100">{status.chat_id}</span>
                        {status.username && (
                            <span className="text-slate-400"> · @{status.username}</span>
                        )}
                        {status.linked_at && (
                            <div className="text-[10px] text-slate-400 mt-0.5">
                                {new Date(status.linked_at).toLocaleString('tr-TR')}'den beri
                            </div>
                        )}
                    </div>
                    <div className="flex gap-2">
                        <button
                            onClick={sendTest}
                            disabled={loading}
                            data-testid="telegram-send-test-btn"
                            className="inline-flex items-center gap-1.5 h-8 px-3 rounded-md border border-sky-500/40 bg-sky-500/10 text-sky-200 hover:bg-sky-500/20 hover:text-sky-100 text-xs font-medium transition-colors disabled:opacity-40"
                        >
                            <TestTube2 className="h-3.5 w-3.5" /> Test Mesajı Gönder
                        </button>
                        <button
                            onClick={unlink}
                            data-testid="telegram-unlink-btn"
                            className="inline-flex items-center gap-1.5 h-8 px-3 rounded-md border border-slate-600 text-slate-300 hover:bg-rose-500/10 hover:text-rose-300 hover:border-rose-500/40 text-xs font-medium transition-colors"
                        >
                            <Link2Off className="h-3.5 w-3.5" /> Bağlantıyı Kaldır
                        </button>
                    </div>
                    <div className="text-[10px] text-slate-400 flex items-start gap-1.5 leading-snug">
                        <Bell className="h-3 w-3 mt-0.5 shrink-0" />
                        <span>
                            Aktif bildirimler: ⭐ favorilerinin TP1/TP2 veya SL seviyelerine değmesi
                            · 🔥 güven ≥ %80 olan yeni fırsatlar.
                        </span>
                    </div>
                </div>
            ) : pairData ? (
                <div className="space-y-3">
                    <div className="text-[11px] text-slate-300 leading-relaxed">
                        <ol className="list-decimal list-inside space-y-1">
                            <li>
                                Telegram'ı aç →{' '}
                                <a
                                    href={`https://t.me/${pairData.bot_username}`}
                                    target="_blank"
                                    rel="noreferrer"
                                    data-testid="telegram-bot-link"
                                    className="text-sky-300 underline hover:text-sky-200"
                                >
                                    @{pairData.bot_username}
                                </a>{' '}
                                botunu aç.
                            </li>
                            <li>
                                Bota şu komutu yaz:
                                <div className="mt-1 flex items-center gap-2 bg-slate-900 border border-slate-700 rounded-md px-2 py-1.5">
                                    <code
                                        data-testid="telegram-pair-command"
                                        className="font-mono text-sm text-sky-200 flex-1"
                                    >
                                        /baglan {pairData.code}
                                    </code>
                                    <button
                                        onClick={copyCommand}
                                        data-testid="telegram-copy-command-btn"
                                        title="Komutu kopyala"
                                        className="h-6 w-6 flex items-center justify-center rounded-sm text-slate-300 hover:bg-slate-700 hover:text-slate-100"
                                    >
                                        <Copy className="h-3 w-3" />
                                    </button>
                                </div>
                            </li>
                            <li>
                                Bot "✅ Bağlantı başarılı!" dediğinde bu panel otomatik olarak güncellenecek.
                            </li>
                        </ol>
                    </div>
                    <div className="flex items-center justify-between text-[10px] text-slate-400">
                        <div>
                            Kodun geçerliliği: <span className="text-amber-300 font-mono">
                                {Math.floor(pairingCountdown / 60)}:{String(pairingCountdown % 60).padStart(2, '0')}
                            </span>
                        </div>
                        <button
                            onClick={() => setPairData(null)}
                            data-testid="telegram-cancel-pair-btn"
                            className="inline-flex items-center gap-1 text-slate-400 hover:text-rose-300"
                        >
                            <X className="h-3 w-3" /> Vazgeç
                        </button>
                    </div>
                </div>
            ) : (
                <div>
                    <button
                        onClick={startPair}
                        disabled={loading}
                        data-testid="telegram-connect-btn"
                        className="inline-flex items-center gap-1.5 h-9 px-4 rounded-md bg-sky-500 hover:bg-sky-400 text-white text-sm font-medium transition-colors disabled:opacity-40"
                    >
                        {loading ? (
                            <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                            <Send className="h-4 w-4" />
                        )}
                        Telegram'a Bağlan
                    </button>
                    <p className="text-[11px] text-slate-400 mt-2 leading-snug">
                        1 dakikada telefonunu bağla, favori coinlerin ve sistemin tespit ettiği
                        yüksek ihtimal fırsatları telefonuna gelsin — dashboard'u açık tutmana gerek kalmasın.
                    </p>
                </div>
            )}
        </div>
    );
}
